import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

const sanitizeFilename = (s) => (s || "").toString().trim().replace(/[^\w.\-]+/g, "-");

/**
 * Normalise an order PDF URL for duplicate detection. Trims whitespace and
 * strips trailing slashes so that links that point at the same file but differ
 * cosmetically are treated as identical. Shared with the case export flow so
 * the `ngtOrders` array and the Files folder dedupe against the same key.
 */
export const normalizeNgtUrl = (u) => (u || "").toString().trim().replace(/\/+$/, "");

/**
 * Get-or-create the per-case "Orders" folder so re-exports don't pile up
 * duplicate folders. Match is keyed on caseId + folder name + lawyer_uid.
 */
const ensureOrdersFolder = async ({ caseId, caseNo, userUid }) => {
  const snap = await getDocs(
    query(
      collection(db, "folders"),
      where("caseId", "==", caseId),
      where("name", "==", "Orders"),
      where("lawyer_uid", "==", userUid)
    )
  );
  if (!snap.empty) return snap.docs[0].id;

  const created = await addDoc(collection(db, "folders"), {
    name: "Orders",
    caseId,
    caseNo: caseNo || "",
    lawyer_uid: userUid,
    createdAt: new Date().toISOString(),
  });
  return created.id;
};

/**
 * Register every PDF found in the NGT listing history as a file under the
 * case's "Orders" folder.
 *
 * Why URLs and not blob uploads: the upstream `greentribunal.gov.in` PDFs are
 * served without CORS, so the browser can't fetch them as blobs to re-upload
 * to Firebase Storage. We instead store the original `pdfPath` as `file_url`
 * — `CaseFiles.jsx` already routes any non-firebase URL through its built-in
 * `needsProxy()` PDF proxy, so viewing/downloading from inside the portal
 * works transparently.
 *
 * - File name: `{dateOfUpload}_order.pdf` (sanitised; `_2`, `_3` suffix if
 *   two listings share the same dateOfUpload).
 * - Silent on individual failures — counted, never thrown.
 *
 * @returns {Promise<{uploaded:number, failed:number, total:number}>}
 */
export const uploadNgtOrderPdfs = async ({
  user,
  caseId,
  caseNo,
  caseCollection,
  listingHistory,
}) => {
  if (!user || !caseId || !Array.isArray(listingHistory)) {
    return { uploaded: 0, failed: 0, skipped: 0, total: 0 };
  }
  const candidates = listingHistory.filter((h) => h && h.pdfPath);
  if (candidates.length === 0) return { uploaded: 0, failed: 0, skipped: 0, total: 0 };

  // Authoritative dedupe: skip any PDF whose URL is already registered as a
  // file on this case, regardless of what the caller passed in. This is the
  // safety net that prevents duplicate order files from ever piling up.
  let existingFileUrls = new Set();
  try {
    const existingSnap = await getDocs(
      query(
        collection(db, "files"),
        where("caseId", "==", caseId),
        where("lawyer_uid", "==", user.uid)
      )
    );
    existingFileUrls = new Set(
      existingSnap.docs.map((d) => normalizeNgtUrl(d.data().file_url)).filter(Boolean)
    );
  } catch (err) {
    console.error("ngtFileSync: failed to load existing files for dedupe:", err);
  }

  const entries = candidates.filter((h) => !existingFileUrls.has(normalizeNgtUrl(h.pdfPath)));
  const skipped = candidates.length - entries.length;
  if (entries.length === 0) {
    return { uploaded: 0, failed: 0, skipped, total: candidates.length };
  }

  let folderId;
  try {
    folderId = await ensureOrdersFolder({ caseId, caseNo, userUid: user.uid });
  } catch (err) {
    console.error("ngtFileSync: ensureOrdersFolder failed:", err);
    return { uploaded: 0, failed: entries.length, skipped, total: candidates.length };
  }

  // Disambiguate filenames when two listings share the same dateOfUpload
  const filenameSeen = new Map();
  const buildName = (h, idx) => {
    const datePart = sanitizeFilename(h.dateOfUpload) || `order-${h.sNo || idx + 1}`;
    const base = `${datePart}_order.pdf`;
    const count = (filenameSeen.get(base) || 0) + 1;
    filenameSeen.set(base, count);
    return count === 1 ? base : `${datePart}_order_${count}.pdf`;
  };

  const results = await Promise.all(
    entries.map(async (h, idx) => {
      const fileName = buildName(h, idx);
      try {
        await addDoc(collection(db, "files"), {
          caseNo: caseNo || "",
          caseId,
          fileName,
          file_url: h.pdfPath,
          lawyer_uid: user.uid,
          uploadedAt: new Date().toISOString(),
          folderId,
          source: "ngt-listing",
        });
        return { ok: true, url: h.pdfPath };
      } catch (err) {
        console.error("ngtFileSync: failed to register PDF:", err);
        return { ok: false };
      }
    })
  );

  const newUrls = results.filter((r) => r.ok).map((r) => r.url);
  const uploaded = newUrls.length;
  const failed = entries.length - uploaded;

  if (newUrls.length > 0 && caseCollection) {
    try {
      const caseRef = doc(db, caseCollection, caseId);
      const snap = await getDoc(caseRef);
      if (snap.exists()) {
        await updateDoc(caseRef, { files: arrayUnion(...newUrls) });
      }
    } catch (err) {
      console.error("ngtFileSync: failed to update case files[]:", err);
    }
  }

  return { uploaded, failed, skipped, total: candidates.length };
};
